import { type ReactNode, type ElementType, useRef, useEffect } from 'react';

import { Flex, ScrollShadow } from '@lib/ui';
import { cn } from '@lib/utils';
import { SidebarMenuProvider } from './SidebarMenu.context';

export interface SidebarMenuProps {
  /** Menu items as children */
  children: ReactNode;
  /** Currently active item ID */
  activeId?: string;
  /** Called when an item is selected */
  onSelect?: (id: string) => void;
  /** Link component to use for navigation (e.g., React Router's Link) */
  linkComponent?: ElementType;
  /** Additional className */
  className?: string;
  /** Header content to display above the menu */
  header?: ReactNode;
  /** Footer content to display below the menu */
  footer?: ReactNode;
  /** Called when item is clicked on mobile to close sidebar */
  onMobileClose?: () => void;
  /** Whether we're on mobile breakpoint (enables auto-close on click) */
  isMobile?: boolean;
  /** Whether to auto-scroll to the active item on mount */
  autoScrollToActive?: boolean;
  /** Scrollbar style: 'default' (native), 'custom' (styled thin scrollbar), 'hidden' (no scrollbar) */
  scrollbar?: 'default' | 'custom' | 'hidden';
  /** Whether to show scroll shadow indicators */
  scrollShadow?: boolean;
}

export function SidebarMenu({
  children,
  activeId,
  onSelect,
  linkComponent = 'a',
  className,
  header,
  footer,
  onMobileClose,
  isMobile = false,
  autoScrollToActive = true,
  scrollbar = 'custom',
  scrollShadow = true,
}: SidebarMenuProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to active item on mount
  useEffect(() => {
    if (!autoScrollToActive || !activeId || !scrollContainerRef.current) return;

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const activeElement = scrollContainerRef.current?.querySelector(
        '[data-sidebar-active="true"]'
      );
      if (activeElement) {
        activeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [autoScrollToActive, activeId]);

  return (
    <SidebarMenuProvider
      value={{ linkComponent, onSelect, activeId, onMobileClose, isMobile, scrollContainerRef }}
    >
      <Flex as="nav" direction="column" className={cn('h-full w-64 bg-surface', className)}>
        {header && <div className="p-4">{header}</div>}

        <ScrollShadow
          ref={scrollContainerRef}
          scrollbar={scrollbar}
          isEnabled={scrollShadow}
          size="sm"
          className="flex-1 py-4"
        >
          <Flex direction="column" gap="1">
            {children}
          </Flex>
        </ScrollShadow>

        {footer && <div className="border-t border-line p-4">{footer}</div>}
      </Flex>
    </SidebarMenuProvider>
  );
}

SidebarMenu.displayName = 'SidebarMenu';
